import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './About.css';

const featureExamples = [
  {
    id: 1,
    title: 'Create Mental Clarity',
    userInput: 'My head feels full of unfinished thoughts.',
    aiResponse: 'I’ve helped you bring everything into one place and organize it into clear groups, so you’re no longer holding it all in your head.',
  },
  {
    id: 2,
    title: 'Turn Thoughts into Structure',
    userInput: 'I need to plan a product launch, but it feels overwhelming.',
    aiResponse: 'I’ve organized your thoughts into a clear structure: goals, ideas, priorities, and open questions. You now have a clear starting point.',
  },
  {
    id: 3,
    title: 'Backlog for Later',
    userInput: 'I have ideas I don’t want to lose, but not now.',
    aiResponse: 'Those ideas are saved and organized in your backlog. They’re out of your mind, but not lost.',
  },
  {
    id: 4,
    title: 'Reduce Overthinking',
    userInput: 'I keep thinking about too many things at once.',
    aiResponse: 'I’ve helped you separate what needs attention now from what can wait, so your mind feels lighter.',
  },
  {
    id: 5,
    title: 'See What Matters',
    userInput: 'What should I focus on today?',
    aiResponse: 'Here’s a clear view of what matters most right now, with everything else safely organized in the background.',
  },
  {
    id: 6,
    title: 'Connect Ideas into Direction',
    userInput: 'My ideas feel related, but unclear.',
    aiResponse: 'I’ve connected related thoughts and organized them into themes, helping you see direction instead of noise.',
  },
];

function About() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featureExamples.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const currentFeature = featureExamples[currentIndex];

  const cardVariants = {
    initial: { opacity: 0, y: 30, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -30, scale: 0.95 },
  };

  const textVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <section id="about" className="landing-about">
      <div className="landing-about-container">
        <motion.h2
          className="landing-about-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          What is Clarity2Work?
        </motion.h2>

        <motion.p
          className="landing-about-intro"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Clarity2Work takes care of remembering, organizing, and executing for you.
          <br />
          See how it works:
        </motion.p>

        <div className="landing-about-features">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="landing-about-feature-card"
              variants={cardVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.5 }}
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <h3 className="landing-about-feature-title">{currentFeature.title}</h3>
              
              <motion.div
                className="landing-about-chat-bubble landing-about-chat-bubble--user"
                variants={textVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: 0.2 }}
              >
                <p className="landing-about-chat-text">{currentFeature.userInput}</p>
              </motion.div>

              <motion.div
                className="landing-about-chat-bubble landing-about-chat-bubble--ai"
                variants={textVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: 0.4 }}
              >
                <div className="landing-about-ai-label">
                  <span className="landing-about-ai-icon">⚡</span>
                  <span className="landing-about-ai-text" style={{marginRight:"8px"}}>CLARITY2WORK</span>
                </div>
                <p className="landing-about-chat-text">{currentFeature.aiResponse}</p>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <div className="landing-about-nav">
            {featureExamples.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`landing-about-nav-dot ${index === currentIndex ? 'landing-about-nav-dot--active' : ''}`}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsAutoPlaying(false);
                  setTimeout(() => setIsAutoPlaying(true), 5000);
                }}
                aria-label={`Go to feature ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <motion.div
          className="landing-about-conclusion"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="landing-about-conclusion-text">
            It feels less like using a product,
          </p>
          <p className="landing-about-conclusion-text landing-about-conclusion-text--emphasis">
            and more like having a mind that stays organized, remembers, and guides you forward.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export default About;
